import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { DA_NavigationPageSimple } from "@danishagro/shared/src/interfaces/navigationPage.interface";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_VetMedicationImageLink } from "@danishagro/shared/src/components/molecules/VetMedication/VetMedication.component";
import { DA_VetMedication } from "@danishagro/shared/src/interfaces/vetMedication.interface";
import { DA_PageFeature } from "@danishagro/shared/src/interfaces/pageFeature.interface";

interface PageHook {
    allPages: Omit<DA_NavigationPageSimple, "children">[];
    pageTree: DA_NavigationPageSimple[];
    currentPathDetails: ({ index: number } & Omit<DA_NavigationPageSimple, "children">)[];
    pageIsLoading: boolean;
    setPageIsLoading: (state: boolean) => void;
    setVetMedication: (data: DA_VetMedication) => void;
    getCategoryId: (categoryPath: string) => string;
    getCategoryFromId: (categoryId: string) => DA_NavigationPageSimple;
    getCategoryPathFromId: (categoryId: string) => string;
    getCategoryFacetsFromId: (categoryId: string) => string[];
    // --- new properties for feature handling ---
    features: DA_PageFeature[];
    getFeatureState: (featureName: string) => DA_PageFeature;
    getAllUniqueFacets: () => string[];
}

const PageContext = React.createContext<PageHook>({
    allPages: [],
    pageTree: [],
    currentPathDetails: [],
    pageIsLoading: false,
    setPageIsLoading: () => null,
    setVetMedication: () => null,
    getCategoryId: () => null,
    getCategoryFromId: () => null,
    getCategoryPathFromId: () => null,
    getCategoryFacetsFromId: () => null,
    features: [],
    getFeatureState: () => ({ feature: "", activated: true, version: "", modules: [] }),
    getAllUniqueFacets: () => [],
});

type Props = {
    children: React.ReactNode;
};

const createPageArray = (pageArray: DA_NavigationPageSimple[]): DA_NavigationPageSimple[] => {
    const pages = [];

    (pageArray || []).map(({ children, ...current }) => {
        pages.push(current);

        if (children && children.length) {
            pages.push(...createPageArray(children));
        }
    });

    return pages;
};

export const PageProvider = ({ children }: Props): JSX.Element => {
    const [pageIsLoading, setPageIsLoading] = useState(false);
    const [currentPathDetails, setCurrentPathDetails] = useState([]);
    const [vetMedication, setVetMedication] = useState<DA_VetMedication>();
    const { navigationObj } = useAppData();
    const { pathname } = useLocation();
    const allPages: DA_NavigationPageSimple[] = createPageArray(navigationObj);
    // const [localFeatures, setLocalFeatures] = useState<DA_PageFeature[]>([]);
    // const { getFeatures } = useFeaturesShopApi();

    // useEffect(() => {
    //     if (window.location.hostname === "localhost") {
    //         // For example, location=shop, company=shoppen
    //         getFeatures("shop", "DanishAgro").then((apiFeatures) => {
    //             // Convert them to DA_PageFeature shape
    //             const mapped: DA_PageFeature[] = apiFeatures.map((feat) => ({
    //                 feature: feat.alias,
    //                 version: feat.version || "",
    //                 // activated: feat.activated,
    //                 activated: true, // TEMP OVERRIDE
    //                 modules: [],
    //             }));
    //             setLocalFeatures(mapped);
    //         });
    //     }
    // }, [pathname, getFeatures]);

    // const features = useMemo<DA_PageFeature[]>(() => {
    //     if (window.location.hostname === "localhost") {
    //         return localFeatures;
    //     } else {
    //         // Otherwise use pageContent.modules (your existing logic)
    //         const pageContent = globalThis?.pageContent;
    //         if (pageContent && pageContent.modules && Array.isArray(pageContent.modules)) {
    //             return (
    //                 pageContent.modules
    //                     // eslint-disable-next-line @typescript-eslint/no-explicit-any
    //                     .map((mod: any) => ({
    //                         feature: mod.content?.alias || "",
    //                         version: mod.version || "",
    //                         activated: true,
    //                         modules: mod.modules || [],
    //                     }))
    //                     // eslint-disable-next-line @typescript-eslint/no-explicit-any
    //                     .filter((mod: any) => mod.feature)
    //             );
    //         }
    //         return [];
    //     }
    // }, [localFeatures]);

    // TODO: Handle features states/versions from server
    const features = useMemo<DA_PageFeature[]>(() => {
        return [];
    }, []);

    const getFeatureState = useCallback(
        (featureName: string): DA_PageFeature => {
            return (
                features.find((f: DA_PageFeature) => f.feature === featureName) || {
                    feature: featureName,
                    activated: true, // defaulting to true if not defined
                    version: "",
                    modules: [],
                }
            );
        },
        [features]
    );

    const explorePath = useCallback((pathNames: string[], tree: DA_NavigationPageSimple[]) => {
        const indexes = [];
        tree.find(({ children, ...pageData }, index) => {
            if (pageData?.path) {
                const pathEnd = pageData.path.match(/\/?[^/]+\/?$/) || [pageData.path];
                if (pathEnd?.length && pathEnd[0].replace(/^\/|\/$/g, "") === pathNames[0]) {
                    indexes.push({ index, ...pageData });
                    if (typeof pathNames[1] === "string" && children) {
                        indexes.push(...explorePath(pathNames.slice(1), children));
                    }
                    return true;
                }
            }
        });
        return indexes;
    }, []);

    const getCategoryId = useCallback(
        (categoryPath: string): string => {
            const [result] = currentPathDetails.filter(
                (pathName) => pathName.path.replace(/^\/|\/$/, "") === categoryPath
            );

            return result?.categoryId?.toString() || undefined;
        },
        [currentPathDetails]
    );

    const getCategoryFromId = useCallback(
        (categoryId: string): DA_NavigationPageSimple => {
            return allPages.find((item) => item.categoryId?.toString() === categoryId);
        },
        [allPages]
    );

    const getCategoryPathFromId = useCallback(
        (categoryId: string): string => {
            const result = allPages.find((item) => item.categoryId?.toString() === categoryId);
            return result ? result.path : undefined;
        },
        [allPages]
    );

    const getCategoryFacetsFromId = useCallback(
        (categoryId: string): string[] => {
            const result = allPages.find((item) => item.categoryId?.toString() === categoryId);
            return result ? result.facets : [];
        },
        [allPages]
    );

    const getAllUniqueFacets = useCallback((): string[] => {
        const facetSet = new Set<string>();

        allPages.forEach((page) => {
            if (page.facets) {
                page.facets.forEach((facet) => facetSet.add(facet));
            }
        });

        return Array.from(facetSet);
    }, [allPages]);

    useEffect(() => {
        const pathNames = pathname.replace(/^\/|\/$/, "").split("/");
        const scopeIndexes = explorePath(pathNames, navigationObj);
        setCurrentPathDetails(scopeIndexes);
    }, [pathname, explorePath, navigationObj]);

    return (
        <PageContext.Provider
            value={{
                allPages,
                pageTree: navigationObj,
                currentPathDetails,
                pageIsLoading,
                setPageIsLoading,
                setVetMedication,
                getCategoryId,
                getCategoryFromId,
                getCategoryPathFromId,
                getCategoryFacetsFromId,
                features,
                getFeatureState,
                getAllUniqueFacets,
            }}
        >
            {children}

            {vetMedication?.labelUrl && vetMedication?.siteUrl && (
                <DA_VetMedicationImageLink
                    pathname={pathname}
                    link={vetMedication.siteUrl}
                    logo={vetMedication.labelUrl}
                />
            )}
        </PageContext.Provider>
    );
};

export const usePage = (): PageHook => React.useContext(PageContext);
